import React, { useEffect } from 'react'
import {
  StyleProperty,
  StyledContainer,
  StyledContinueButtonContainer,
  StyledPropertyLabel,
  StyledPropertyValue,
  StyledSummaryContainer,
} from '../../ProductCheckout/ProductCheckout.styled'
import valueIcon from 'assets/images/icons/SprengnetterAppsValue.png'
import { useTranslation } from 'react-i18next'
import { selectAddressFormData, setAddressData } from 'slices/addressFormSlice'
import { useSelector } from 'react-redux'
import checkMarkYes from 'assets/images/icons/checkMarkYes.svg'
import checkMarkNo from 'assets/images/icons/checkMarkNo.svg'
import Button from 'components/Button/Button'
import { useCreateAddressMutation, useUpdateAddressMutation } from 'services/address-api'
import { sanitizeAddressData } from '../../helper'
import { useAppDispatch } from 'store/hooks'
import { useToastEffect } from 'components/Toast/hooks/useToastEffect'
import {
  BasementType,
  BuildingType,
  // EquipmentMatrixHeating,
  EquipmentMatrixSurfaceMountedInstallation,
  ModernizationLevelType,
  ModernizationMethodType,
  ObjectType,
  OwnershipType,
  RentalCurrentUsageType,
} from 'types/ApiResponse'
import { EquipmentMethod } from 'types/EquipmentMethod'

const ValueSummary = (props: any) => {
  const { setCurrentSubTab, setCurrentTab, isModernizationDisabled } = props
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const addressFormData = useSelector(selectAddressFormData)
  const objectType = addressFormData.addressData?.propertyDetails?.objectType
  const buildingType = addressFormData.addressData?.propertyDetails?.buildingType
  // const energyCertificateIssued =
  //   addressFormData.addressData?.propertyDetails?.energyCertificate !== undefined
  // const energyCertificate = addressFormData.addressData?.propertyDetails?.energyCertificate
  const equipmentMatrix = addressFormData.addressData?.propertyDetails?.equipmentMatrix
  const modernizationMatrix = addressFormData.addressData?.propertyDetails?.modernizationMatrix
  const numberOfUnitsLabel =
    objectType === ObjectType.APARTMENT
      ? t('newAddressModal.objectDataTab.numberOfUnits')
      : t('newAddressModal.objectDataTab.numberOfResidentialUnits')

  const [
    createAddress,
    {
      isLoading: isCrateAddressLoading,
      isSuccess: isCrateAddressSuccess,
      isError: isCrateAddressError,
      data: createAddressData,
    },
  ] = useCreateAddressMutation()

  const [
    updateAddress,
    {
      isLoading: isUpdateAddressLoading,
      isSuccess: isUpdateAddressSuccess,
      isError: isUpdateAddressError,
      data: updateAddressData,
    },
  ] = useUpdateAddressMutation()

  const isLoading = isUpdateAddressLoading || isCrateAddressLoading

  const getBooleanValue = (value?: boolean) => {
    return (
      <>
        {`${value}` === `true` ? (
          <img src={checkMarkYes} alt="checkMarkYes" />
        ) : `${value}` === `false` ? (
          <img src={checkMarkNo} alt="checkMarkNo" />
        ) : null}
        {`${value}` === `true` ? t('common.yes') : `${value}` === `false` ? t('common.no') : '—'}
      </>
    )
  }

  const getModernizationValue = (value: ModernizationLevelType) => {
    return t(`modernizationLevel.${value?.toLowerCase()}`)
  }

  const onBackClick = () => {
    const tabsCount = objectType === ObjectType.PLOT_OF_LAND ? 3 : isModernizationDisabled ? 2 : 1
    setCurrentTab((tab) => tab - tabsCount)
  }

  const onContinueClick = () => {
    const data = sanitizeAddressData(addressFormData.addressData)
    if (addressFormData.isEdit) {
      updateAddress({
        id: addressFormData.addressData.id,
        payload: data,
      })
    } else {
      createAddress(data)
    }
  }

  useEffect(() => {
    if (isCrateAddressSuccess) {
      dispatch(setAddressData(createAddressData))
      setCurrentSubTab((tab) => tab + 1)
    }
  }, [isCrateAddressSuccess, createAddressData, setCurrentSubTab, dispatch])

  useEffect(() => {
    if (isUpdateAddressSuccess) {
      dispatch(setAddressData(updateAddressData))
      setCurrentSubTab((tab) => tab + 1)
    }
  }, [isUpdateAddressSuccess, updateAddressData, setCurrentSubTab, dispatch])

  useToastEffect(t('newAddressModal.addressTab.create-address.error.toast'), 'ERROR', [
    isCrateAddressError || isUpdateAddressError,
  ])

  return (
    <>
      <StyledContainer>
        <img src={valueIcon} alt="" className="app-icon" />
        <h1>{t('common.generateValue')}</h1>
        <h2>{t('checkoutTab.summary')}</h2>
        <StyledSummaryContainer>
          <StyleProperty>
            <StyledPropertyLabel>{t('leadsList.addressFilter')}</StyledPropertyLabel>
            <StyledPropertyValue>
              {`${addressFormData.addressData.street} ${addressFormData.addressData.houseNumber}`}
              <br />
              {`${addressFormData.addressData.town}, ${addressFormData.addressData.zip}`}
              <br />
              {`${addressFormData.addressData.nation}`}
            </StyledPropertyValue>
          </StyleProperty>
          {/* <StyleProperty>
            <StyledPropertyLabel>{t('newAddressModal.valuationDate')}</StyledPropertyLabel>
            <StyledPropertyValue>{addressFormData.addressData?.valuationDate}</StyledPropertyValue>
          </StyleProperty> */}
          <StyleProperty>
            <StyledPropertyLabel>
              {t('newAddressModal.objectDataTab.objectType')}
            </StyledPropertyLabel>
            <StyledPropertyValue>{t(`objectType.${objectType.toLowerCase()}`)}</StyledPropertyValue>
          </StyleProperty>
          {(objectType === ObjectType.HOUSE || objectType === ObjectType.MULTI_FAMILY_HOUSE) && (
            <StyleProperty>
              <StyledPropertyLabel>
                {t('newAddressModal.objectDataTab.buildingType')}
              </StyledPropertyLabel>
              <StyledPropertyValue>
                {t(
                  `buildingType.${addressFormData.addressData?.propertyDetails?.buildingType.toLowerCase()}`
                )}
              </StyledPropertyValue>
            </StyleProperty>
          )}
          {objectType === ObjectType.HOUSE && (
            <StyleProperty>
              <StyledPropertyLabel>
                {t('newAddressModal.objectDataTab.houseType')}
              </StyledPropertyLabel>
              <StyledPropertyValue>
                {t(
                  `houseOrApartmentType.${addressFormData.addressData.propertyDetails?.houseOrApartmentType.toLowerCase()}`
                )}
              </StyledPropertyValue>
            </StyleProperty>
          )}
          {objectType !== ObjectType.PLOT_OF_LAND && (
            <>
              <StyleProperty>
                <StyledPropertyLabel>
                  {t('newAddressModal.objectDataTab.buildYear')}
                </StyledPropertyLabel>
                <StyledPropertyValue>
                  {addressFormData.addressData?.propertyDetails?.constructionYear}
                </StyledPropertyValue>
              </StyleProperty>
              <StyleProperty>
                <StyledPropertyLabel>
                  {t('newAddressModal.objectDataTab.livingArea')}
                </StyledPropertyLabel>
                <StyledPropertyValue>
                  {`${addressFormData.addressData?.propertyDetails?.livingArea} m²`}
                </StyledPropertyValue>
              </StyleProperty>
            </>
          )}
          {objectType === ObjectType.APARTMENT ? null : (
            <StyleProperty>
              <StyledPropertyLabel>
                {t('newAddressModal.objectDataTab.plotArea')}
              </StyledPropertyLabel>
              <StyledPropertyValue>
                {`${addressFormData.addressData?.propertyDetails?.plotArea} m²`}
              </StyledPropertyValue>
            </StyleProperty>
          )}
          {objectType !== ObjectType.PLOT_OF_LAND && (
            <>
              {buildingType === BuildingType.RESIDENTIAL_AND_COMMERCIAL && (
                <StyleProperty>
                  <StyledPropertyLabel>
                    {t('newAddressModal.objectDataTab.commercialArea')}
                  </StyledPropertyLabel>
                  <StyledPropertyValue>
                    {`${addressFormData.addressData?.propertyDetails?.commercialArea} m²`}
                  </StyledPropertyValue>
                </StyleProperty>
              )}
              {objectType !== ObjectType.APARTMENT && (
                <StyleProperty>
                  <StyledPropertyLabel>
                    {t('newAddressModal.objectDataTab.numberOfFloors')}
                  </StyledPropertyLabel>
                  <StyledPropertyValue>
                    {addressFormData.addressData?.propertyDetails?.numberOfFloors
                      ? addressFormData.addressData?.propertyDetails?.numberOfFloors
                      : '—'}
                  </StyledPropertyValue>
                </StyleProperty>
              )}
              {objectType !== ObjectType.HOUSE && (
                <StyleProperty>
                  <StyledPropertyLabel>{numberOfUnitsLabel}</StyledPropertyLabel>
                  <StyledPropertyValue>
                    {addressFormData.addressData?.propertyDetails?.numberOfUnits
                      ? addressFormData.addressData?.propertyDetails?.numberOfUnits
                      : '—'}
                  </StyledPropertyValue>
                </StyleProperty>
              )}
              {buildingType === BuildingType.RESIDENTIAL_AND_COMMERCIAL && (
                <StyleProperty>
                  <StyledPropertyLabel>
                    {t('newAddressModal.objectDataTab.numberOfCommercialUnits')}
                  </StyledPropertyLabel>
                  <StyledPropertyValue>
                    {addressFormData.addressData?.propertyDetails?.numberOfCommercialUnits}
                  </StyledPropertyValue>
                </StyleProperty>
              )}
              {objectType === ObjectType.HOUSE && (
                <StyleProperty>
                  <StyledPropertyLabel>
                    {t('newAddressModal.objectDataTab.construction')}
                  </StyledPropertyLabel>
                  <StyledPropertyValue>
                    {t(
                      `constructionType.${addressFormData.addressData.propertyDetails?.construction.toLowerCase()}`
                    )}
                  </StyledPropertyValue>
                </StyleProperty>
              )}
              {objectType === ObjectType.HOUSE && (
                <StyleProperty>
                  <StyledPropertyLabel>
                    {t('newAddressModal.objectDataTab.roof')}
                  </StyledPropertyLabel>
                  <StyledPropertyValue>
                    {t(
                      `roofType.${addressFormData.addressData.propertyDetails?.roof.toLowerCase()}`
                    )}
                  </StyledPropertyValue>
                </StyleProperty>
              )}
              {objectType === ObjectType.HOUSE && (
                <StyleProperty>
                  <StyledPropertyLabel>
                    {t('newAddressModal.objectDataTab.basement')}
                  </StyledPropertyLabel>
                  <StyledPropertyValue>
                    {t(
                      `basementType.${addressFormData.addressData.propertyDetails?.basement.toLowerCase()}`
                    )}
                  </StyledPropertyValue>
                </StyleProperty>
              )}
              {objectType === ObjectType.HOUSE &&
                addressFormData.addressData?.propertyDetails?.basement !== BasementType.NONE && (
                  <StyleProperty>
                    <StyledPropertyLabel>
                      {t('newAddressModal.objectDataTab.basementConversion')}
                    </StyledPropertyLabel>
                    <StyledPropertyValue>
                      {t(
                        `basementConversionType.${addressFormData.addressData.propertyDetails?.basementConversion.toLowerCase()}`
                      )}
                    </StyledPropertyValue>
                  </StyleProperty>
                )}
              {objectType === ObjectType.HOUSE && (
                <StyleProperty>
                  <StyledPropertyLabel>
                    {t('newAddressModal.objectDataTab.outdoorFacilities')}
                  </StyledPropertyLabel>
                  <StyledPropertyValue>
                    {t(
                      `outdoorFacilitiesType.${addressFormData.addressData.propertyDetails?.outdoorFacilities.toLowerCase()}`
                    )}
                  </StyledPropertyValue>
                </StyleProperty>
              )}
              {objectType === ObjectType.APARTMENT && (
                <StyleProperty>
                  <StyledPropertyLabel>
                    {t('newAddressModal.objectDataTab.floor')}
                  </StyledPropertyLabel>
                  <StyledPropertyValue>
                    {addressFormData.addressData?.propertyDetails?.floorNumber
                      ? addressFormData.addressData?.propertyDetails?.floorNumber
                      : '—'}
                  </StyledPropertyValue>
                </StyleProperty>
              )}
              {objectType === ObjectType.APARTMENT ||
              objectType === ObjectType.MULTI_FAMILY_HOUSE ? (
                <StyleProperty>
                  <StyledPropertyLabel>
                    {t('newAddressModal.objectDataTab.elevator')}
                  </StyledPropertyLabel>
                  <StyledPropertyValue>
                    {getBooleanValue(addressFormData.addressData?.propertyDetails?.elevator)}
                  </StyledPropertyValue>
                </StyleProperty>
              ) : null}
              {objectType === ObjectType.APARTMENT && (
                <StyleProperty>
                  <StyledPropertyLabel>
                    {t('newAddressModal.objectDataTab.garden')}
                  </StyledPropertyLabel>
                  <StyledPropertyValue>
                    {getBooleanValue(addressFormData.addressData?.propertyDetails?.garden)}
                  </StyledPropertyValue>
                </StyleProperty>
              )}
              {objectType !== ObjectType.PLOT_OF_LAND && (
                <StyleProperty>
                  <StyledPropertyLabel>
                    {t('newAddressModal.objectDataTab.garage')}
                  </StyledPropertyLabel>
                  <StyledPropertyValue>
                    {addressFormData.addressData?.propertyDetails?.garages
                      ? addressFormData.addressData?.propertyDetails?.garages
                      : '—'}
                  </StyledPropertyValue>
                </StyleProperty>
              )}
              {objectType !== ObjectType.HOUSE && (
                <StyleProperty>
                  <StyledPropertyLabel>
                    {t('newAddressModal.objectDataTab.parkingPlace')}
                  </StyledPropertyLabel>
                  <StyledPropertyValue>
                    {addressFormData.addressData?.propertyDetails?.outdoorParkingSpaces
                      ? addressFormData.addressData?.propertyDetails?.outdoorParkingSpaces
                      : '—'}
                  </StyledPropertyValue>
                </StyleProperty>
              )}
              <StyleProperty>
                <StyledPropertyLabel>
                  {t('newAddressModal.objectDataTab.energyCertificate.energyClass')}
                </StyledPropertyLabel>
                <StyledPropertyValue>
                  {addressFormData.addressData?.propertyDetails?.energyCertificate?.energyClass
                    ? t(
                        `energyClassTypes.${addressFormData.addressData?.propertyDetails?.energyCertificate?.energyClass.toLowerCase()}`
                      )
                    : '—'}
                </StyledPropertyValue>
              </StyleProperty>
              <StyleProperty>
                <StyledPropertyLabel>
                  {t('newAddressModal.objectDataTab.ownership')}
                </StyledPropertyLabel>
                <StyledPropertyValue>
                  {t(
                    `ownership.${addressFormData.addressData?.propertyDetails?.ownership?.toLowerCase()}`
                  )}
                </StyledPropertyValue>
              </StyleProperty>
              {addressFormData.addressData?.propertyDetails?.ownership !== OwnershipType.FULL && (
                <>
                  <StyleProperty>
                    <StyledPropertyLabel>
                      {t('newAddressModal.objectDataTab.groundLeaseActualRent')}
                    </StyledPropertyLabel>
                    <StyledPropertyValue>
                      {addressFormData.addressData?.propertyDetails?.groundLeaseActualRent
                        ? `${
                            addressFormData.addressData?.propertyDetails?.groundLeaseActualRent
                          } ${t('newAddressModal.objectDataTab.groundLeaseActualRent.unit')}`
                        : '—'}
                    </StyledPropertyValue>
                  </StyleProperty>
                  <StyleProperty>
                    <StyledPropertyLabel>
                      {t('newAddressModal.objectDataTab.groundLeaseRemainingTerm')}
                    </StyledPropertyLabel>
                    <StyledPropertyValue>
                      {addressFormData.addressData?.propertyDetails?.groundLeaseRemainingTerm
                        ? `${
                            addressFormData.addressData?.propertyDetails?.groundLeaseRemainingTerm
                          } ${t('newAddressModal.objectDataTab.groundLeaseRemainingTerm.unit')}`
                        : '—'}
                    </StyledPropertyValue>
                  </StyleProperty>
                </>
              )}
              {objectType !== ObjectType.MULTI_FAMILY_HOUSE && (
                <StyleProperty>
                  <StyledPropertyLabel>
                    {t('newAddressModal.objectDataTab.rentalCurrentUsage')}
                  </StyledPropertyLabel>
                  <StyledPropertyValue>
                    {addressFormData.addressData?.propertyDetails?.rentalCurrentUsage
                      ? t(
                          `rentalCurrentUsage.${addressFormData.addressData?.propertyDetails?.rentalCurrentUsage?.toLowerCase()}`
                        )
                      : '—'}
                  </StyledPropertyValue>
                </StyleProperty>
              )}
              {objectType === ObjectType.APARTMENT &&
                addressFormData.addressData?.propertyDetails?.rentalCurrentUsage !==
                  RentalCurrentUsageType.OWNER_OCCUPIED && (
                  <StyleProperty>
                    <StyledPropertyLabel>
                      {t('newAddressModal.objectDataTab.suitableForOwnUse')}
                    </StyledPropertyLabel>
                    <StyledPropertyValue>
                      {getBooleanValue(
                        addressFormData.addressData?.propertyDetails?.suitableForOwnUse
                      )}
                    </StyledPropertyValue>
                  </StyleProperty>
                )}
              {objectType !== ObjectType.HOUSE && (
                <>
                  <StyleProperty>
                    <StyledPropertyLabel>
                      {t('newAddressModal.objectDataTab.rentalInformation.actualRent')}
                    </StyledPropertyLabel>
                    <StyledPropertyValue>
                      {addressFormData.addressData?.propertyDetails?.rentalInformation?.actualRent
                        ? addressFormData.addressData?.propertyDetails?.rentalInformation
                            ?.actualRent
                        : '—'}
                    </StyledPropertyValue>
                  </StyleProperty>
                  <StyleProperty>
                    <StyledPropertyLabel>
                      {t('newAddressModal.objectDataTab.rentalInformation.actualRentReference')}
                    </StyledPropertyLabel>
                    <StyledPropertyValue>
                      {addressFormData.addressData?.propertyDetails?.rentalInformation
                        ?.actualRentReference
                        ? t(
                            `rentalReference.${addressFormData.addressData?.propertyDetails?.rentalInformation?.actualRentReference?.toLowerCase()}`
                          )
                        : '—'}
                    </StyledPropertyValue>
                  </StyleProperty>
                </>
              )}
              {buildingType === BuildingType.RESIDENTIAL_AND_COMMERCIAL && (
                <>
                  <StyleProperty>
                    <StyledPropertyLabel>
                      {t('newAddressModal.objectDataTab.rentalInformation.actualCommercialRent')}
                    </StyledPropertyLabel>
                    <StyledPropertyValue>
                      {addressFormData.addressData?.propertyDetails?.rentalInformation
                        ?.actualCommercialRent
                        ? addressFormData.addressData?.propertyDetails?.rentalInformation
                            ?.actualCommercialRent
                        : '—'}
                    </StyledPropertyValue>
                  </StyleProperty>
                  <StyleProperty>
                    <StyledPropertyLabel>
                      {t(
                        'newAddressModal.objectDataTab.rentalInformation.actualCommercialRentReference'
                      )}
                    </StyledPropertyLabel>
                    <StyledPropertyValue>
                      {addressFormData.addressData?.propertyDetails?.rentalInformation
                        ?.actualCommercialRentReference
                        ? t(
                            `rentalReference.${addressFormData.addressData?.propertyDetails?.rentalInformation?.actualCommercialRentReference?.toLowerCase()}`
                          )
                        : '—'}
                    </StyledPropertyValue>
                  </StyleProperty>
                </>
              )}
              {objectType !== ObjectType.MULTI_FAMILY_HOUSE &&
                addressFormData.addressData?.propertyDetails?.ownership !== OwnershipType.FULL &&
                addressFormData.addressData?.propertyDetails?.ownership !== OwnershipType.LEASE && (
                  <StyleProperty>
                    <StyledPropertyLabel>
                      {t('newAddressModal.objectDataTab.headers.coownership')}
                    </StyledPropertyLabel>
                    <StyledPropertyValue>
                      {addressFormData.addressData?.propertyDetails?.coownershipNumerator
                        ? addressFormData.addressData?.propertyDetails?.coownershipNumerator
                        : '—'}
                      {' / '}
                      {addressFormData.addressData?.propertyDetails?.coownershipDenominator
                        ? addressFormData.addressData?.propertyDetails?.coownershipDenominator
                        : '—'}
                    </StyledPropertyValue>
                  </StyleProperty>
                )}
              {/* <>
                <StyleProperty>
                  <StyledPropertyLabel>
                    {t('newAddressModal.objectDataTab.energyCertificate')}
                  </StyledPropertyLabel>
                  <StyledPropertyValue>
                    {getBooleanValue(energyCertificateIssued)}
                  </StyledPropertyValue>
                </StyleProperty>
                {energyCertificateIssued && (
                  <>
                    <StyleProperty>
                      <StyledPropertyLabel>
                        {t('newAddressModal.objectDataTab.energyCertificate.energyClass')}
                      </StyledPropertyLabel>
                      <StyledPropertyValue>
                        {energyCertificate?.energyClass
                          ? t(`energyClassTypes.${energyCertificate?.energyClass.toLowerCase()}`)
                          : '—'}
                      </StyledPropertyValue>
                    </StyleProperty>
                    <StyleProperty>
                      <StyledPropertyLabel>
                        {t('newAddressModal.objectDataTab.energyCertificate.certificateType')}
                      </StyledPropertyLabel>
                      <StyledPropertyValue>
                        {energyCertificate?.certificateType
                          ? t(
                              `energyCertificateTypes.${energyCertificate?.certificateType.toLowerCase()}`
                            )
                          : '—'}
                      </StyledPropertyValue>
                    </StyleProperty>
                    <StyleProperty>
                      <StyledPropertyLabel>
                        {t('newAddressModal.objectDataTab.energyCertificate.issueDate')}
                      </StyledPropertyLabel>
                      <StyledPropertyValue>
                        {energyCertificate?.issueDate ? energyCertificate?.issueDate : '—'}
                      </StyledPropertyValue>
                    </StyleProperty>
                    <StyleProperty>
                      <StyledPropertyLabel>
                        {t('newAddressModal.objectDataTab.energyCertificate.primaryEnergyDemand')}
                      </StyledPropertyLabel>
                      <StyledPropertyValue>
                        {energyCertificate?.primaryEnergyDemand
                          ? `${energyCertificate?.primaryEnergyDemand} kWh/(m²·a)`
                          : '—'}
                      </StyledPropertyValue>
                    </StyleProperty>
                    <StyleProperty>
                      <StyledPropertyLabel>
                        {t('newAddressModal.objectDataTab.energyCertificate.finalEnergyDemand')}
                      </StyledPropertyLabel>
                      <StyledPropertyValue>
                        {energyCertificate?.finalEnergyDemand
                          ? `${energyCertificate?.finalEnergyDemand} kWh/(m²·a)`
                          : '—'}
                      </StyledPropertyValue>
                    </StyleProperty>
                    <StyleProperty>
                      <StyledPropertyLabel>
                        {t('newAddressModal.objectDataTab.energyCertificate.co2Emission')}
                      </StyledPropertyLabel>
                      <StyledPropertyValue>
                        {energyCertificate?.co2Emission
                          ? `${energyCertificate?.co2Emission} CO₂/(m²·a)`
                          : '—'}
                      </StyledPropertyValue>
                    </StyleProperty>
                  </>
                )}
              </> */}
              <StyleProperty>
                <StyledPropertyLabel>{t('summary.equipmentMethod')}</StyledPropertyLabel>
                <StyledPropertyValue>
                  {t(
                    `newAddressModal.furnishingTab.equipmentMethod.${addressFormData.addressData?.propertyDetails?.equipmentMethod?.toLowerCase()}`
                  )}
                </StyledPropertyValue>
              </StyleProperty>
              {addressFormData.addressData?.propertyDetails?.equipmentMethod ===
                EquipmentMethod.SIMPLE && (
                <StyleProperty>
                  <StyledPropertyLabel>{t('addressModal.equipmentLevel')}</StyledPropertyLabel>
                  <StyledPropertyValue>
                    {t(
                      `equipmentValue.${addressFormData.addressData?.propertyDetails?.equipmentValue?.toLowerCase()}`
                    )}
                  </StyledPropertyValue>
                </StyleProperty>
              )}
              {addressFormData.addressData?.propertyDetails?.equipmentMethod ===
                EquipmentMethod.DETAILED && (
                <>
                  <StyleProperty>
                    <StyledPropertyLabel>
                      {t('newAddressModal.furnishingTab.heatingOptions.label')}
                    </StyledPropertyLabel>
                    <StyledPropertyValue>
                      {t(`equipmentMatrix.heating.${equipmentMatrix?.heating?.toLowerCase()}`)}
                    </StyledPropertyValue>
                  </StyleProperty>
                  {/* {(equipmentMatrix?.heating === EquipmentMatrixHeating.FLOOR_HEATING ||
                    equipmentMatrix?.heating === EquipmentMatrixHeating.CENTRAL_HEATING) && (
                    <StyleProperty>
                      <StyledPropertyLabel>
                        {t('newAddressModal.furnishingTab.heatingSystem')}
                      </StyledPropertyLabel>
                      <StyledPropertyValue>
                        {getBooleanValue(equipmentMatrix?.heatingSystem)}
                      </StyledPropertyValue>
                    </StyleProperty>
                  )} */}
                  {objectType !== ObjectType.APARTMENT && (
                    <StyleProperty>
                      <StyledPropertyLabel>
                        {t('newAddressModal.furnishingTab.roofOptions.label')}
                      </StyledPropertyLabel>
                      <StyledPropertyValue>
                        {equipmentMatrix?.roofCovering
                          ? t(
                              `equipmentMatrix.roofCovering.${equipmentMatrix?.roofCovering?.toLowerCase()}`
                            )
                          : '—'}
                      </StyledPropertyValue>
                    </StyleProperty>
                  )}
                  <StyleProperty>
                    <StyledPropertyLabel>
                      {t('newAddressModal.furnishingTab.windowsOptions.label')}
                    </StyledPropertyLabel>
                    <StyledPropertyValue>
                      {equipmentMatrix?.windows
                        ? t(`equipmentMatrix.windows.${equipmentMatrix?.windows?.toLowerCase()}`)
                        : '—'}
                    </StyledPropertyValue>
                  </StyleProperty>
                  {(objectType === ObjectType.APARTMENT ||
                    objectType === ObjectType.MULTI_FAMILY_HOUSE) && (
                    <StyleProperty>
                      <StyledPropertyLabel>
                        {t('newAddressModal.furnishingTab.storageOptions.label')}
                      </StyledPropertyLabel>
                      <StyledPropertyValue>
                        {equipmentMatrix?.storeRoom
                          ? t(
                              `equipmentMatrix.storeRoom.${equipmentMatrix?.storeRoom?.toLowerCase()}`
                            )
                          : '—'}
                      </StyledPropertyValue>
                    </StyleProperty>
                  )}
                  {objectType === ObjectType.APARTMENT && (
                    <StyleProperty>
                      <StyledPropertyLabel>
                        {t('newAddressModal.furnishingTab.floorCoveringOptions.label')}
                      </StyledPropertyLabel>
                      <StyledPropertyValue>
                        {equipmentMatrix?.floorCovering
                          ? t(
                              `equipmentMatrix.floorCovering.${equipmentMatrix?.floorCovering?.toLowerCase()}`
                            )
                          : '—'}
                      </StyledPropertyValue>
                    </StyleProperty>
                  )}
                  {objectType !== ObjectType.HOUSE && (
                    <StyleProperty>
                      <StyledPropertyLabel>
                        {t('newAddressModal.furnishingTab.outdoorLivingAreaOptions.label')}
                      </StyledPropertyLabel>
                      <StyledPropertyValue>
                        {equipmentMatrix?.residentialArea
                          ? t(
                              `equipmentMatrix.residentialArea.${equipmentMatrix?.residentialArea?.toLowerCase()}`
                            )
                          : '—'}
                      </StyledPropertyValue>
                    </StyleProperty>
                  )}
                  {objectType !== ObjectType.MULTI_FAMILY_HOUSE && (
                    <StyleProperty>
                      <StyledPropertyLabel>
                        {t('newAddressModal.furnishingTab.bathOptions.label')}
                      </StyledPropertyLabel>
                      <StyledPropertyValue>
                        {equipmentMatrix?.bathroom
                          ? t(
                              `equipmentMatrix.bathroom.${equipmentMatrix?.bathroom?.toLowerCase()}`
                            )
                          : '—'}
                      </StyledPropertyValue>
                    </StyleProperty>
                  )}
                  <StyleProperty>
                    <StyledPropertyLabel>
                      {t('newAddressModal.furnishingTab.separateGuestToilet')}
                    </StyledPropertyLabel>
                    <StyledPropertyValue>
                      {getBooleanValue(equipmentMatrix?.guestToilet)}
                    </StyledPropertyValue>
                  </StyleProperty>
                  <StyleProperty>
                    <StyledPropertyLabel>
                      {t('newAddressModal.furnishingTab.insulatedOuterWalls')}
                    </StyledPropertyLabel>
                    <StyledPropertyValue>
                      {getBooleanValue(equipmentMatrix?.insulatedExteriorWalls)}
                    </StyledPropertyValue>
                  </StyleProperty>
                  <StyleProperty>
                    <StyledPropertyLabel>
                      {t('newAddressModal.furnishingTab.linesOnPlaster')}
                    </StyledPropertyLabel>
                    <StyledPropertyValue>
                      {getBooleanValue(
                        equipmentMatrix?.surfaceMountedInstallation ===
                          EquipmentMatrixSurfaceMountedInstallation.CABLES_ON_WALLS
                      )}
                    </StyledPropertyValue>
                  </StyleProperty>
                  {objectType === ObjectType.HOUSE && (
                    <>
                      <StyleProperty>
                        <StyledPropertyLabel>
                          {t('newAddressModal.furnishingTab.fireplace')}
                        </StyledPropertyLabel>
                        <StyledPropertyValue>
                          {getBooleanValue(equipmentMatrix?.fireplace)}
                        </StyledPropertyValue>
                      </StyleProperty>
                      <StyleProperty>
                        <StyledPropertyLabel>
                          {t('newAddressModal.furnishingTab.sauna')}
                        </StyledPropertyLabel>
                        <StyledPropertyValue>
                          {getBooleanValue(equipmentMatrix?.sauna)}
                        </StyledPropertyValue>
                      </StyleProperty>
                    </>
                  )}
                </>
              )}
              {addressFormData.addressData?.propertyDetails?.constructionYear <=
                new Date().getFullYear() - 10 && (
                <>
                  <StyleProperty>
                    <StyledPropertyLabel>{t('summary.modernizationMethod')}</StyledPropertyLabel>
                    <StyledPropertyValue>
                      {t(
                        `modernizationMethod.${addressFormData.addressData?.propertyDetails?.modernizationMethod?.toLowerCase()}`
                      )}
                    </StyledPropertyValue>
                  </StyleProperty>
                  {addressFormData.addressData?.propertyDetails?.modernizationMethod !==
                    ModernizationMethodType.AUTOMATIC && (
                    <StyleProperty>
                      <StyledPropertyLabel>
                        {t('newAddressModal.modernizationTab.refurbishmentYear')}
                      </StyledPropertyLabel>
                      <StyledPropertyValue>
                        {addressFormData.addressData?.propertyDetails?.refurbishmentYear
                          ? addressFormData.addressData?.propertyDetails?.refurbishmentYear
                          : '—'}
                      </StyledPropertyValue>
                    </StyleProperty>
                  )}
                  {addressFormData.addressData?.propertyDetails?.modernizationMethod ===
                    ModernizationMethodType.SIMPLE && (
                    <StyleProperty>
                      <StyledPropertyLabel>
                        {t('addressModal.modernizationClass')}
                      </StyledPropertyLabel>
                      <StyledPropertyValue>
                        {t(
                          `modernizationValue.${addressFormData.addressData?.propertyDetails?.modernizationValue?.toLowerCase()}`
                        )}
                      </StyledPropertyValue>
                    </StyleProperty>
                  )}
                  {addressFormData.addressData?.propertyDetails?.modernizationMethod ===
                    ModernizationMethodType.COSTS && (
                    <StyleProperty>
                      <StyledPropertyLabel>
                        {t('newAddressModal.modernizationTab.modernizationCost')}
                      </StyledPropertyLabel>
                      <StyledPropertyValue>
                        {addressFormData.addressData?.propertyDetails?.modernizationCost}
                      </StyledPropertyValue>
                    </StyleProperty>
                  )}
                  {addressFormData.addressData?.propertyDetails?.modernizationMethod ===
                    ModernizationMethodType.DETAILED && (
                    <>
                      <StyleProperty>
                        <StyledPropertyLabel>
                          {t('newAddressModal.modernizationTab.interiorDesign.label')}
                        </StyledPropertyLabel>
                        <StyledPropertyValue>
                          {getModernizationValue(modernizationMatrix?.interiorDesign)}
                        </StyledPropertyValue>
                      </StyleProperty>
                      <StyleProperty>
                        <StyledPropertyLabel>
                          {t('newAddressModal.modernizationTab.sanitaryArea.label')}
                        </StyledPropertyLabel>
                        <StyledPropertyValue>
                          {getModernizationValue(modernizationMatrix?.sanitaryArea)}
                        </StyledPropertyValue>
                      </StyleProperty>
                      <StyleProperty>
                        <StyledPropertyLabel>
                          {t('newAddressModal.modernizationTab.heatingSystem.label')}
                        </StyledPropertyLabel>
                        <StyledPropertyValue>
                          {getModernizationValue(modernizationMatrix?.heatingSystem)}
                        </StyledPropertyValue>
                      </StyleProperty>
                      <StyleProperty>
                        <StyledPropertyLabel>
                          {t('newAddressModal.modernizationTab.pipes.label')}
                        </StyledPropertyLabel>
                        <StyledPropertyValue>
                          {getModernizationValue(modernizationMatrix?.pipes)}
                        </StyledPropertyValue>
                      </StyleProperty>
                      <StyleProperty>
                        <StyledPropertyLabel>
                          {t('newAddressModal.modernizationTab.windows.label')}
                        </StyledPropertyLabel>
                        <StyledPropertyValue>
                          {getModernizationValue(modernizationMatrix?.windows)}
                        </StyledPropertyValue>
                      </StyleProperty>
                      <StyleProperty>
                        <StyledPropertyLabel>
                          {t('newAddressModal.modernizationTab.thermalInsulation.label')}
                        </StyledPropertyLabel>
                        <StyledPropertyValue>
                          {getModernizationValue(modernizationMatrix?.thermalInsulation)}
                        </StyledPropertyValue>
                      </StyleProperty>
                      <StyleProperty>
                        <StyledPropertyLabel>
                          {t('newAddressModal.modernizationTab.roofCoveringAndInsulation.label')}
                        </StyledPropertyLabel>
                        <StyledPropertyValue>
                          {getModernizationValue(modernizationMatrix?.roofCoveringAndInsulation)}
                        </StyledPropertyValue>
                      </StyleProperty>
                      <StyleProperty>
                        <StyledPropertyLabel>
                          {t('newAddressModal.modernizationTab.roomLayout.label')}
                        </StyledPropertyLabel>
                        <StyledPropertyValue>
                          {getModernizationValue(modernizationMatrix?.roomLayout)}
                        </StyledPropertyValue>
                      </StyleProperty>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </StyledSummaryContainer>
      </StyledContainer>
      <StyledContinueButtonContainer>
        <Button kind="primary" onClick={() => onBackClick()}>
          {t('common.backButton')}
        </Button>
        <Button
          kind="primary"
          loading={isLoading}
          disabled={isLoading}
          onClick={() => onContinueClick()}
        >
          {isLoading ? t('common.loadingButton') : t('common.saveAndProceedButton')}
        </Button>
      </StyledContinueButtonContainer>
    </>
  )
}

export default ValueSummary
